/**
 * Pilot Dashboard
 * Main screen for pilots - shows role selection and job board
 */

import React, { useState, useEffect } from 'react';
import PilotJobBoardEnhanced from './PilotJobBoardEnhanced';
import PilotDeliveryScreen from './PilotDeliveryScreen';
import PilotProfileSettings from './PilotProfileSettings';
import './PilotDashboard.css';

const PilotDashboard = ({ apiBaseUrl, token, user, onLogout }) => {
  const [view, setView] = useState('job-board'); // 'job-board' or 'errand'
  const [currentJob, setCurrentJob] = useState(null);
  const [errandStatus, setErrandStatus] = useState('idle'); // idle, in_progress, completed
  const [showSettings, setShowSettings] = useState(false);
  const [stats, setStats] = useState({
    totalErrands: 0,
    completedToday: 0,
    earnings: 0,
    rating: 4.8,
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {  
    fetchPilotStats(); // fetchPilotStats is defined below, intentional
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const fetchPilotStats = async () => {
    try {
      const response = await fetch(`${apiBaseUrl}/api/v1/pilots/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching pilot stats:', err);
    }
  };

  const handleJobAccepted = (job) => {
    setCurrentJob(job);
    setView('errand');
    setErrandStatus('in_progress');
  };

  const handleErrandComplete = async () => {
    setErrandStatus('completed');
    setView('job-board');
    setCurrentJob(null);
    setTimeout(() => {
      setErrandStatus('idle');
    }, 2000);
  };

  return (
    <div className="pilot-dashboard">
      {/* Header */}
      <div className="pilot-header">
        <div className="header-content">
          <div className="pilot-logo-section">
            <img src="/logo.png" alt="ErrandBridge Logo" className="pilot-logo" />
          </div>
          <div className="pilot-info">
            <h1>{user?.first_name || 'Pilot'} Dashboard</h1>
            <p className="pilot-id">ID: {user?.id}</p>
          </div>
          <div className="header-actions">
            <button 
              className="settings-btn"
              onClick={() => setShowSettings(true)}
              title="Profile Settings"
            >
              ⚙️ Settings
            </button>
            <button className="logout-btn" onClick={onLogout}>
              🚪 Logout
            </button>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="pilot-stats">
        <div className="stat-item">
          <span className="stat-label">📦 Total Errands</span>
          <span className="stat-value">{stats.totalErrands}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">✅ Today</span>
          <span className="stat-value">{stats.completedToday}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">💰 Earnings</span>
          <span className="stat-value">₦{stats.earnings.toLocaleString()}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">⭐ Rating</span>
          <span className="stat-value">{stats.rating}</span>
        </div>
      </div>

      {/* Status Message */}
      {errandStatus === 'completed' && (
        <div className="success-message">
          ✅ Errand completed! Great job! 🎉
        </div>
      )}

      {/* Main Content */}
      {view === 'job-board' ? (
        <PilotJobBoardEnhanced
          apiBaseUrl={apiBaseUrl}
          token={token}
          pilotId={user?.id}
          user={user}
          onJobAccepted={handleJobAccepted}
          currentStatus={errandStatus}
          onLogout={onLogout}
        />
      ) : (
        <div className="errand-view">
          <button 
            className="back-btn"
            onClick={() => setView('job-board')}
          >
            ← Back to Jobs
          </button>
          {currentJob && (
            <PilotDeliveryScreen
              errand={currentJob}
              token={token}
              onDeliveryComplete={handleErrandComplete}
            />
          )}
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <PilotProfileSettings
          user={user}
          token={token}
          apiBaseUrl={apiBaseUrl}
          onClose={() => setShowSettings(false)}
          onSave={() => {
            fetchPilotStats();
            setShowSettings(false);
          }}
        />
      )}
    </div>
  );
};

export default PilotDashboard;
